// Provider-neutral V3 contract. No runtime imports: shared with the client/tests.
export const PEOPLE_TASKS_PROVIDERS = ["google_contacts", "microsoft_contacts", "apple_contacts", "google_tasks", "microsoft_todo", "apple_reminders"] as const;
export type PeopleTaskProvider = typeof PEOPLE_TASKS_PROVIDERS[number];
export type PeopleTaskAction = "probe" | "lists" | "search" | "get" | "create" | "update" | "complete" | "reopen" | "delete";
export interface PeopleTaskRequest {
  provider: PeopleTaskProvider;
  action: PeopleTaskAction;
  query?: string;
  cursor?: string;
  limit?: number;
  list_id?: string;
  item_id?: string;
  version?: string;
  title?: string;
  notes?: string;
  due_date?: string | null;
  due_date_time?: string | null;
  due_time_zone?: "UTC";
  concurrency_acknowledged?: true;
  delete_acknowledged?: true;
  mutation_contract?: 2;
  email?: string;
  phone?: string;
  idempotency_key?: string;
  confirmed?: boolean;
  device_id?: string;
  mirror_context?: { session_id: string; action_id: string };
}
export interface PeopleTaskItem {
  id: string;
  title: string;
  version?: string;
  list_id?: string;
  email?: string;
  phone?: string;
  notes?: string;
  due_date?: string | null;
  completed?: boolean;
  read_only?: boolean;
}
export const isContactProvider = (p: string) => p.endsWith("_contacts");
export const isNativePeopleProvider = (p: string) => p === "apple_contacts" || p === "apple_reminders";
export const isPeopleTaskWrite = (a: string) => ["create", "update", "complete", "reopen", "delete"].includes(a);
export const isCloudPeopleProvider = (p: string): p is PeopleTaskProvider => PEOPLE_TASKS_PROVIDERS.includes(p as PeopleTaskProvider) && !isNativePeopleProvider(p);

export function peopleTaskScopes(provider: PeopleTaskProvider, write = false): string[] {
  if (provider === "google_contacts") return ["https://www.googleapis.com/auth/contacts" + (write ? "" : ".readonly")];
  if (provider === "google_tasks") return ["https://www.googleapis.com/auth/tasks" + (write ? "" : ".readonly")];
  if (provider === "microsoft_contacts") return ["offline_access", write ? "Contacts.ReadWrite" : "Contacts.Read"];
  if (provider === "microsoft_todo") return ["offline_access", write ? "Tasks.ReadWrite" : "Tasks.Read"];
  return [];
}
export function hasPeopleTaskScopes(provider: PeopleTaskProvider, scopes: unknown, requested: string[]): boolean {
  if (!Array.isArray(scopes) || !isCloudPeopleProvider(provider)) return false;
  const normalize = (scope: string) => provider.startsWith("google_")
    ? scope : scope.replace(/^https:\/\/graph\.microsoft\.com\//i, "").toLowerCase();
  const granted = new Set(scopes.filter((s): s is string => typeof s === "string").map(normalize));
  const required = requested.filter(s => s !== "offline_access").map(normalize);
  const read = normalize(peopleTaskScopes(provider).find(s => s !== "offline_access")!);
  const write = normalize(peopleTaskScopes(provider, true).find(s => s !== "offline_access")!);
  // Both providers document that this specific write scope also permits reads.
  return required.length > 0 && required.every(s => granted.has(s) || (s === read && granted.has(write)));
}
export function hasPeopleTaskWriteScope(provider: PeopleTaskProvider, scopes: unknown): boolean {
  return hasPeopleTaskScopes(provider, scopes, peopleTaskScopes(provider, true));
}
export class PeopleTaskError extends Error {
  constructor(message: string, public status = 400) { super(message); }
}
export type CloudTaskProvider = "google_tasks" | "microsoft_todo";
export const isCloudTaskProvider = (p: string): p is CloudTaskProvider => p === "google_tasks" || p === "microsoft_todo";
export interface CloudTaskList {
  id: string; title: string; read_only: boolean; create_writable: boolean; content_truncated: boolean;
  is_owner?: boolean; is_shared?: boolean;
}
export interface CloudTaskRecord {
  id: string; list_id: string; title: string; notes: string; version: string; status: string;
  completed: boolean; read_only: boolean; writable: boolean; delete_writable: boolean; content_truncated: boolean;
  assigned: boolean; recurring: boolean | null; recurrence_known: boolean; parent_id: string | null;
  due_date: string | null; due_kind: "scheduled_date" | "due_date_time";
  due_date_time: string | null; due_time_zone: string | null;
  write_actions: ("update" | "complete" | "reopen")[]; write_restriction?: string;
}
export function taskUnsafeControl(value: string, multiline = false): boolean {
  return Array.from(value).some(c => {
    const n = c.codePointAt(0)!;
    return (n < 32 && !(multiline && [9, 10, 13].includes(n))) || (n >= 127 && n <= 159);
  });
}
// Provider IDs are opaque path segments, never URLs, aliases, or dot segments.
export function cloudTaskId(value: unknown): string {
  if (typeof value !== "string" || !value || value !== value.trim() || value.length > 1000 || taskUnsafeControl(value) ||
    value === "." || value === ".." || /^[a-z][a-z\d+.-]*:/i.test(value) || value === "@default") throw new PeopleTaskError("invalid_task_id");
  return value;
}
export function cloudTaskVersion(value: unknown, google = false): string {
  if (typeof value !== "string" || !value || value !== value.trim() || value.length > 2000 || taskUnsafeControl(value) || value.includes("*")) throw new PeopleTaskError("invalid_task_version");
  if (google && (value.length < 3 || value[0] !== '"' || value.at(-1) !== '"' || /["\\,]/.test(value.slice(1, -1)))) throw new PeopleTaskError("invalid_task_version");
  return value;
}
// Call before claiming quota/receipts, as well as before adapter IO.
// Google Tasks' performance guide documents current ETag / If-Match for PATCH.
// DELETE and Graph mutations require an acknowledged read/compare flow;
// this does not promise an atomic conditional operation.
export function assertCloudTaskCapability(b: PeopleTaskRequest): void {
  if (!isCloudTaskProvider(b.provider)) return;
  const compare = b.action === "delete" || b.provider === "microsoft_todo" && ["update", "complete", "reopen"].includes(b.action);
  if (compare && b.concurrency_acknowledged !== true) throw new PeopleTaskError("concurrency_acknowledgement_required", 403);
  if (b.action === "delete" && b.delete_acknowledged !== true) throw new PeopleTaskError("delete_acknowledgement_required", 403);
  if (b.provider === "microsoft_todo" && b.action === "create" && b.due_date != null) throw new PeopleTaskError("microsoft_due_date_timezone_required", 403);
}
export function validatePeopleTaskRequest(raw: unknown): PeopleTaskRequest {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new PeopleTaskError("invalid_request");
  const b = raw as Record<string, unknown>;
  if (!PEOPLE_TASKS_PROVIDERS.includes(b.provider as PeopleTaskProvider)) throw new PeopleTaskError("unsupported_provider");
  if (!["probe", "lists", "search", "get", "create", "update", "complete", "reopen", "delete"].includes(String(b.action))) throw new PeopleTaskError("unsupported_action");
  const out: Record<string, unknown> = { provider: b.provider, action: b.action };
  const limits: Record<string, number> = { query: 200, cursor: 4096, list_id: 1000, item_id: 1000, version: 2000, title: 200, notes: 8000, email: 254, phone: 80, idempotency_key: 220, device_id: 100 };
  for (const [key, max] of Object.entries(limits)) {
    if (b[key] === undefined) continue;
    if (typeof b[key] !== "string" || (b[key] as string).length > max || /[\u0000]/.test(b[key] as string)) throw new PeopleTaskError("invalid_" + key);
    out[key] = key === "notes" ? b[key] : (b[key] as string).trim();
  }
  const contact = isContactProvider(String(b.provider));
  if (contact && ["lists", "complete", "reopen", "delete"].includes(String(b.action))) throw new PeopleTaskError("unsupported_action");
  if (b.due_date !== undefined) {
    if (b.due_date !== null && (typeof b.due_date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(b.due_date) || !Number.isFinite(Date.parse(b.due_date)) || new Date(b.due_date).toISOString().slice(0, 10) !== b.due_date)) throw new PeopleTaskError("invalid_due_date");
    out.due_date = b.due_date;
  }
  if (out.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(out.email))) throw new PeopleTaskError("invalid_email");
  if (b.action === "create" && !out.title) throw new PeopleTaskError("title_required");
  if (["get", "update", "complete", "reopen", "delete"].includes(String(b.action)) && !out.item_id) throw new PeopleTaskError("item_required");
  if (!contact && !["probe", "lists"].includes(String(b.action)) && !out.list_id) throw new PeopleTaskError("list_required");
  if (isPeopleTaskWrite(String(b.action))) {
    if (b.confirmed !== true) throw new PeopleTaskError("confirmation_required");
    if (!out.idempotency_key) throw new PeopleTaskError("idempotency_key_required");
    if (b.action !== "create" && !out.version) throw new PeopleTaskError("version_required");
    if (b.action === "update" && !["title", ...(contact ? ["email", "phone"] : ["notes", "due_date", "due_date_time"])].some(k => b[k] !== undefined)) throw new PeopleTaskError("changes_required");
    if (out.title === "") throw new PeopleTaskError("title_required");
    out.confirmed = true;
    if (isNativePeopleProvider(String(b.provider)) && !/^[0-9a-f-]{36}$/i.test(String(out.device_id || ""))) throw new PeopleTaskError("device_id_required");
  }
  if (contact) { delete out.notes; delete out.due_date; }
  else { delete out.email; delete out.phone; }
  if (isCloudTaskProvider(String(b.provider))) {
    if (b.mutation_contract !== undefined) {
      if (b.mutation_contract !== 2) throw new PeopleTaskError("unsupported_task_mutation_contract");
      out.mutation_contract = 2;
    }
    const compare = b.action === "delete" || b.provider === "microsoft_todo" && ["update", "complete", "reopen"].includes(String(b.action));
    if (compare) { if (b.concurrency_acknowledged !== true) throw new PeopleTaskError("concurrency_acknowledgement_required", 403); out.concurrency_acknowledged = true; }
    else if (b.concurrency_acknowledged !== undefined) throw new PeopleTaskError("unsupported_task_field_concurrency_acknowledged");
    if (b.action === "delete") { if (b.delete_acknowledged !== true) throw new PeopleTaskError("delete_acknowledgement_required", 403); out.delete_acknowledged = true; }
    else if (b.delete_acknowledged !== undefined) throw new PeopleTaskError("unsupported_task_field_delete_acknowledged");
    if (b.due_date_time !== undefined || b.due_time_zone !== undefined) {
      if (b.provider !== "microsoft_todo" || !["create", "update"].includes(String(b.action)) || b.due_date !== undefined) throw new PeopleTaskError("unsupported_task_field_due_date_time");
      if (b.due_date_time === null) {
        if (b.due_time_zone !== undefined) throw new PeopleTaskError("invalid_task_timezone");
        out.due_date_time = null;
      } else {
        if (typeof b.due_date_time !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(b.due_date_time)
          || !Number.isFinite(Date.parse(b.due_date_time + "Z")) || new Date(b.due_date_time + "Z").toISOString().slice(0, 19) !== b.due_date_time) throw new PeopleTaskError("invalid_task_due_datetime");
        if (b.due_time_zone !== "UTC") throw new PeopleTaskError("invalid_task_timezone");
        out.due_date_time = b.due_date_time; out.due_time_zone = "UTC";
      }
    }
    if (b.provider === "microsoft_todo" && b.due_date !== undefined && b.action !== "create") throw new PeopleTaskError("microsoft_due_date_timezone_required", 403);
    if (["delete", "complete", "reopen"].includes(String(b.action)) && ["title", "notes", "due_date", "due_date_time"].some(key => b[key] !== undefined)) throw new PeopleTaskError("unsupported_task_edit_fields");
    if (b.limit !== undefined && (!["lists", "search"].includes(String(b.action)) || !Number.isInteger(b.limit) || Number(b.limit) < 1 || Number(b.limit) > 20)) throw new PeopleTaskError("invalid_limit");
    if (["lists", "search"].includes(String(b.action))) out.limit = b.limit ?? 10;
    for (const key of ["list_id", "item_id"]) if (out[key] !== undefined) out[key] = cloudTaskId(b[key]);
    if (out.version !== undefined) out.version = cloudTaskVersion(b.version, b.provider === "google_tasks");
    for (const key of ["query", "title", "notes", "cursor", "idempotency_key"]) {
      if (typeof out[key] === "string" && taskUnsafeControl(out[key] as string, key === "notes")) throw new PeopleTaskError("invalid_" + key);
    }
    for (const key of ["recurrence", "recurring", "assignment", "assignmentInfo", "checklist", "checklistItems", "parent", "parent_id", "position", "destinationTasklist", "destination_list_id", "move", "status", "completed", "dueDateTime", "time_zone"]) {
      if (b[key] !== undefined) throw new PeopleTaskError("unsupported_task_field_" + key);
    }
    if (b.mirror_context !== undefined) {
      const context = b.mirror_context as Record<string, unknown>;
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!context || typeof context !== "object" || Array.isArray(context) || !uuid.test(String(context.session_id)) || !uuid.test(String(context.action_id))) throw new PeopleTaskError("invalid_mirror_context");
      out.mirror_context = { session_id: context.session_id, action_id: context.action_id };
    }
    assertCloudTaskCapability(out as unknown as PeopleTaskRequest);
  }
  return out as unknown as PeopleTaskRequest;
}

export function cloudPeopleConfig(provider: PeopleTaskProvider) {
  const google = provider.startsWith("google_");
  return {
    authorizeUrl: google ? "https://accounts.google.com/o/oauth2/v2/auth" : "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    tokenUrl: google ? "https://oauth2.googleapis.com/token" : "https://login.microsoftonline.com/common/oauth2/v2.0/token",
    clientKey: google ? "GOOGLE_OAUTH_CLIENT_ID" : "OUTLOOK_OAUTH_CLIENT_ID",
    secretKey: google ? "GOOGLE_OAUTH_CLIENT_SECRET" : "OUTLOOK_OAUTH_CLIENT_SECRET",
  };
}
